/**
 * localDate.ts
 *
 * Local calendar date helpers.
 *
 * WHY THIS FILE EXISTS
 * --------------------
 * `new Date().toISOString().split('T')[0]` returns the UTC calendar date,
 * not the user's. New Zealand runs at UTC+12/+13, so any capture made
 * before midday NZ resolves to the PREVIOUS day. The same applies to
 * slicing any UTC timestamp string to get a date.
 *
 * `entry_date` is a Postgres `date` column with no default: whatever the
 * client sends is stored verbatim. A wrong value here is wrong at rest
 * and corrupts the Timeline permanently.
 *
 * Always use these helpers. Never derive a calendar date from an ISO
 * string by slicing or splitting it.
 */

/** Today's date in the user's local timezone, as YYYY-MM-DD. */
export function todayLocalISO(): string {
  return toLocalISODate(new Date())
}

/**
 * Converts a UTC timestamp (or Date) to the user's LOCAL calendar date,
 * as YYYY-MM-DD.
 */
export function toLocalISODate(input: string | Date): string {
  const d = input instanceof Date ? input : new Date(input)
  if (isNaN(d.getTime())) {
    throw new Error(`toLocalISODate: invalid date input "${String(input)}"`)
  }
  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}